import { useEffect, useState, useCallback } from "react";
import { getCardInfoList } from "../../api/cash/cash.api";
import { Plus } from "lucide-react";
import CardDesign from "./CardDesign";
import CardRegistrationPopup from "./CardRegistrationPopup";
import {useSearchParams} from "react-router-dom";
import useBackGuard from "../../libs/useBackGuard";

/**
 * 온보딩 마지막 단계에서 **자동으로** 카드 등록창을 띄웠는지 기록하는 키.
 *
 * ## 왜 필요한가 (2026-08-21)
 *
 * `/payments?showCardReg=true` 는 마운트 즉시 토스 결제창으로 **페이지를 이동**시킨다
 * (`CardRegistrationPopup` → `payment.requestBillingAuth`).
 * 그래서 고객이 **토스 화면에서 뒤로가기를 누르면** 이 URL 로 돌아오고,
 * 마운트가 다시 일어나 **또 토스로 튕겨 나간다 — 뒤로가기로는 빠져나올 수 없다.**
 *
 * 탭 세션에 "이미 자동으로 띄웠다"를 남겨 **자동 실행은 한 번만** 하게 한다.
 * 돌아온 고객에게는 카드 목록과 '새로운 카드 등록' 타일이 보이므로 스스로 다시 시도할 수 있다.
 *
 * 등록을 마치면 `/payments`(파라미터 없음)로 돌아오므로 그때 이 값을 지워
 * 다음 번 온보딩 진입에서는 정상적으로 자동 실행되게 한다.
 */
const AUTO_OPEN_FLAG = 'omoplay:cardRegAutoOpened';

const CardBillingForm = ({ onSelectedCard }) => {
    const [query] = useSearchParams();
    const showCardReg = query.get("showCardReg");
    const isOnboardingEntry = showCardReg === 'true';

    const [cardInfoItems, setCardInfoItems] = useState([]);
    const [showRegistration, setShowRegistration] = useState(() => {
        if (!isOnboardingEntry) {
            // 정상 진입(등록 완료 후 복귀 포함) — 다음 온보딩을 위해 플래그를 되돌린다.
            sessionStorage.removeItem(AUTO_OPEN_FLAG);
            return false;
        }

        // 토스에서 뒤로 돌아온 경우: 자동 실행하지 않는다(무한 왕복 방지).
        if (sessionStorage.getItem(AUTO_OPEN_FLAG) === '1') return false;

        sessionStorage.setItem(AUTO_OPEN_FLAG, '1');
        return true;
    });
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    /*
      뒤로가기 1차 방어 — **온보딩 진입(`showCardReg=true`)일 때만** 건다.
      사이드바로 들어온 평상시 `/payments` 에서 뒤로가기를 막으면 그냥 불편해진다.
      구현은 `libs/useBackGuard.js`, 위저드와 같은 훅이다.
    */
    useBackGuard(
        "카드 등록을 마치려면 화면의 '새로운 카드 등록' 을 이용해주세요. 뒤로 가면 등록이 중단됩니다.",
        isOnboardingEntry
    );

    const fetchCards = useCallback(() => {
        getCardInfoList().then(({ data }) => {
            setCardInfoItems(data);
            if (data.length > 0) {
                const defaultCard = data.find(c => c.isDefault) || data[0];
                onSelectedCard?.(defaultCard.id);
            }
        });
    }, [onSelectedCard]);

    useEffect(() => {
        fetchCards();
    }, [fetchCards, showRegistration, refreshTrigger]);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    return (
        /* 모바일 여백 축소(2026-08-21) — `p-8`(32px×2)이면 390px 화면에서 320px 카드가 잘린다 */
        <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-5 sm:p-8 my-6">
            <div className="w-full overflow-x-auto pb-6 mt-4 custom-scrollbar">
                <div className="flex items-center gap-6 w-max px-2">
                    {cardInfoItems.map(cardInfo => (
                        <CardDesign
                            key={cardInfo.id}
                            cardInfo={cardInfo}
                            onRefresh={handleRefresh}
                            /* 마지막 1장은 삭제 불가 — 결제수단이 0장이 되면 다음 정산 결제가 실패한다.
                               서버도 같은 규칙으로 막지만(400 + 사유) 버튼 단계에서 먼저 알려준다. */
                            canDelete={cardInfoItems.length > 1}
                        />
                    ))}

                    {/* 새로운 카드 등록 영역 */}
                    <div
                        className="w-[320px] h-[200px] border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 hover:border-gray-400 hover:shadow-inner transition-all duration-300 group"
                        onClick={() => setShowRegistration(true)}
                    >
                        <div className="rounded-full bg-white shadow-sm w-14 h-14 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <Plus className="w-6 h-6 text-gray-400 group-hover:text-gray-700 transition-colors" strokeWidth={2.5} />
                        </div>
                        <span className="mt-4 text-sm font-medium text-gray-500 group-hover:text-gray-700 transition-colors">
                                새로운 카드 등록
                            </span>
                    </div>
                </div>
            </div>

            <CardRegistrationPopup
                show={showRegistration}
                onCancel={() => setShowRegistration(false)}
            />
        </div>
    );
};

export default CardBillingForm;